import { motion } from 'framer-motion';
import { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import AgentHeader from './AgentHeader.js';
import ChatHistory from './ChatHistory.js';
import ChatInputBar from './ChatInputBar.js';
import ChatInputStatusStrip from './ChatInputStatusStrip.js';
import SessionSummaryPanel from './SessionSummaryPanel.js';
import { useChatSocket } from '../../hooks/useChatSocket.js';
import { useChatStore } from '../../stores/useChatStore.js';
import '../../styles/micro-interactions.css';

const SCROLL_THRESHOLD = 50; // pixels from bottom to consider "at bottom"

const ChatShell = () => {
    const { sessionId, addLogChunks, messages } = useChatStore();

    // Scroll tracking state
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [unseenCount, setUnseenCount] = useState(0);
    const lastMessageCountRef = useRef(messages.length);
    const userScrolledUpRef = useRef(false);

    // Check if scroll position is at bottom
    const checkIfAtBottom = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return true;
        const { scrollTop, scrollHeight, clientHeight } = container;
        return scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD;
    }, []);

    // Handle scroll events
    const handleScroll = useCallback(() => {
        const atBottom = checkIfAtBottom();
        setIsAtBottom(atBottom);

        // If user scrolled back to bottom, clear unseen count and reset flag
        if (atBottom) {
            setUnseenCount(0);
            userScrolledUpRef.current = false;
        } else {
            // User scrolled up - disable auto-scroll
            userScrolledUpRef.current = true;
        }
    }, [checkIfAtBottom]);

    // Scroll to bottom smoothly
    const scrollToBottom = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth',
        });
        setUnseenCount(0);
        setIsAtBottom(true);
        userScrolledUpRef.current = false;
    }, []);

    // Track new messages and handle auto-scroll
    useEffect(() => {
        const newMessageCount = messages.length;
        const prevMessageCount = lastMessageCountRef.current;

        if (newMessageCount > prevMessageCount) {
            const newMessages = newMessageCount - prevMessageCount;

            if (userScrolledUpRef.current) {
                // User scrolled up - increment unseen count
                setUnseenCount(prev => prev + newMessages);
            } else {
                // User at bottom - auto-scroll to show new messages
                requestAnimationFrame(() => {
                    const container = scrollContainerRef.current;
                    if (container) {
                        container.scrollTop = container.scrollHeight;
                    }
                });
            }
        }

        lastMessageCountRef.current = newMessageCount;
    }, [messages.length]);

    const chatSocket = useChatSocket({
        sessionId: sessionId || undefined,
        userId: `user-${Math.random().toString(36).substr(2, 9)}`,
        onMessage: message => {
            console.log('[ChatShell] Received chat WebSocket message:', message);
        },
        onChatEcho: message => {
            console.log('[ChatShell] Received chat echo:', message);
        },
        onBeeLogChunk: chunk => {
            console.log('[ChatShell] Received beeLogChunk:', chunk);
            addLogChunks([chunk]);
        },
        onConnectionChange: isConnected => {
            console.log('[ChatShell] Chat WebSocket connection status:', isConnected);
        },
        onOpen: () => {
            console.log('[ChatShell] Chat WebSocket connection opened');
        },
        onError: () => {
            console.log('[ChatShell] Chat WebSocket connection error');
        },
    });

    return (
        <motion.div
            className="flex flex-col h-full min-h-0 w-full bg-gray-900/95 rounded-lg shadow-lg p-4"
            data-devinid="workspace-chat-panel"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
        >
            {/* Messages */}
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto scrollbar-hidden relative"
            >
                <ChatHistory />
                <SessionSummaryPanel />

                {/* Scroll to bottom button - shown when user scrolls up */}
                {!isAtBottom && (
                    <button
                        onClick={scrollToBottom}
                        className="sticky bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-full shadow-lg transition-all duration-200"
                        aria-label={
                            unseenCount > 0
                                ? `Scroll to bottom (${unseenCount} new messages)`
                                : 'Scroll to bottom'
                        }
                        data-testid="scroll-to-bottom-button"
                    >
                        <ChevronDown className="w-4 h-4" />
                        {unseenCount > 0 && (
                            <span
                                className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium bg-blue-500 text-white rounded-full"
                                data-testid="unseen-count-badge"
                            >
                                {unseenCount > 99 ? '99+' : unseenCount}
                            </span>
                        )}
                    </button>
                )}
            </div>

            {/* Chat input - layout only, no visual styling */}
            <div className="flex-none border-t border-slate-800/50">
                <ChatInputBar />
            </div>

            {/* Status strip below chat input - centered, shows Repo, Confidence, System status */}
            <div className="flex-none flex justify-center">
                <ChatInputStatusStrip />
            </div>
        </motion.div>
    );
};

export default ChatShell;
